import { Product } from '../types';
import { analyzeIngredients } from './eNumberService';

// Spice categorization
const SPICE_CATEGORIES = {
  // Generally Halal (if pure and additive-free)
  PURE_SPICES: [
    'cumin', 'turmeric', 'coriander', 'black pepper', 'white pepper',
    'cardamom', 'cloves', 'cinnamon', 'nutmeg', 'ginger', 'garlic',
    'onion', 'paprika', 'saffron', 'oregano', 'basil', 'thyme',
    'rosemary', 'sage', 'bay leaf', 'dill', 'fennel', 'fenugreek',
    'mint', 'parsley', 'cilantro', 'chili', 'cayenne'
  ],
  
  // Require verification (mixed spices, blends)
  PROCESSED_SPICES: [
    'curry powder', 'garam masala', 'taco seasoning', 'mixed spices',
    'spice blend', 'seasoning mix', 'spice extract', 'spice oleoresin',
    'natural spice flavor', 'spice oil'
  ],
  
  // High-risk additives commonly found in spices
  SPICE_ADDITIVES: [
    'anti-caking agent', 'silicon dioxide', 'magnesium stearate',
    'calcium silicate', 'flow agent', 'free-flow agent',
    'maltodextrin', 'modified starch', 'emulsifier'
  ]
};

const HARAM_INGREDIENTS = [
  // Alcohols
  'alcohol',
  'ethanol',
  'ethyl alcohol',
  'isopropyl alcohol',
  'beer',
  'wine',
  'liquor',
  'rum',
  'whiskey',
  'brandy',
  
  // Pork and Derivatives
  'pork',
  'bacon',
  'ham',
  'lard',
  'suet',
  'pepperoni',
  'salami',
  
  // Animal-Derived Ingredients
  'gelatin',
  'gelatine',
  'collagen',
  'e441',
  'blood',
  'hemoglobin',
  'blood plasma',
  'animal fat',
  'animal shortening',
  'animal broth',
  'tallow',
  
  // Insect/Animal Derived Colors and Glazes
  'carmine',
  'cochineal',
  'e120',
  'shellac',
  'e904',
  'confectioner\'s glaze',
  
  // Enzymes (Unless Specified Halal)
  'rennet',
  'pepsin',
  
  // Prohibited Animals
  'carnivorous animals',
  'snake',
  'frog',
  'scorpion',
  
  // E-Numbers (Animal Derived)
  'e471', // Mono and diglycerides from animal fat
  'e542', // Bone phosphate
  'e631', // Disodium inosinate (often from meat)
  'e920', // L-cysteine (from hair/feathers)
  
  // Flavorings with Alcohol
  'fish sauce', // Often contains alcohol
  'soy sauce', // May contain alcohol from fermentation
  
  // Animal By-Products
  'musk', // From deer glands unless synthetic
  'lanolin', // Sheep wool derivative
  'e913', // Lanolin derivatives
  'e901', // Beeswax
];

const MUSHBOOH_INGREDIENTS = [
  // E-Numbers
  'e433', // Polysorbate 80
  'e434', // Polysorbate 40
  'e435', // Polysorbate 60
  'e436', // Polysorbate 65
  'e470', // Fatty acid salts
  'e483', // Stearyl Tartrate
  
  // Ambiguous Sources
  'natural flavors',
  'artificial flavors',
  'enzymes',
  'emulsifier',
  'emulsifiers',
  'shortening',
  'monoglycerides',
  'diglycerides',
  'mono and diglycerides',
  'glycerin',
  'glycerol',
  'stearic acid',
  'magnesium stearate',
  'whey', // May contain rennet
  'lipase', // Unless specified as microbial/plant-based
  'polysorbate 80', // E433, unless certified halal
  'culture', // Source may be unclear
  'cultures', // Source may be unclear
  'lecithin', // Source may be unclear
  'vanilla flavoring',
  'vanilla extract', // Requires verification due to potential alcohol content
  
  // Spice-related (moved from direct inclusion to be handled by spice analysis)
  ...SPICE_CATEGORIES.PROCESSED_SPICES,
  ...SPICE_CATEGORIES.SPICE_ADDITIVES
];

// Cross-contamination warnings
const CROSS_CONTAMINATION_WARNINGS = [
  'may contain traces of pork',
  'processed in a facility that handles pork',
  'processed on shared equipment with alcohol',
  'may contain traces of animal-derived ingredients',
  'manufactured in a facility that processes alcohol',
  'may contain alcohol'
];

// List of trusted halal certification bodies
const TRUSTED_CERTIFIERS = [
  'ifanca',
  'hmc',
  'jakim',
  'muis',
  'mui',
  'halal food council',
  'halal food authority',
  'islamic food and nutrition council',
];

interface OpenFoodFactsProduct {
  status: number;
  status_verbose: string;
  product: {
    _id: string;
    code: string;
    product_name: string;
    brands: string;
    ingredients_text: string;
    image_url: string;
    image_front_url: string;
    image_ingredients_url: string;
    ingredients: Array<{
      id: string;
      text: string;
      vegan?: string;
      vegetarian?: string;
      has_sub_ingredients?: string;
      percent_estimate?: number;
    }>;
    labels: string;
    labels_tags: string[];
    ingredients_analysis_tags?: string[];
  };
}

const API_CONFIG = {
  baseUrl: 'https://world.openfoodfacts.org',
  headers: {
    'User-Agent': 'HalalScanner/1.0 (support@halalscanner.app)',
    'Accept': 'application/json',
  },
  fields: [
    'code',
    'product_name',
    'brands',
    'ingredients',
    'ingredients_text',
    'labels',
    'labels_tags',
    'image_url',
    'image_front_url',
    'image_ingredients_url',
    'ingredients_analysis_tags'
  ].join(','),
};

function isHalalCertified(labels: string[]): { certified: boolean; certifier: string | null } {
  const matchedCertifier = TRUSTED_CERTIFIERS.find(certifier =>
    labels.some(label => label.toLowerCase().includes(certifier))
  );

  return {
    certified: !!matchedCertifier,
    certifier: matchedCertifier || null
  };
}

function isHalalSource(ingredient: string): boolean {
  // Only consider plant/vegetable sources as halal by default
  const halalIndicators = [
    'vegetable',
    'plant',
    'microbial',
    'halal',
    'halal certified',
    'plant-based',
    'vegan',
  ];

  // Special case for gelatin - must be explicitly labeled as halal
  if (ingredient.toLowerCase().includes('gelatin') || ingredient.toLowerCase().includes('gelatine')) {
    return ingredient.toLowerCase().includes('halal gelatin') || 
           ingredient.toLowerCase().includes('halal gelatine') ||
           ingredient.toLowerCase().includes('fish gelatin');
  }

  return halalIndicators.some(indicator => 
    ingredient.toLowerCase().includes(indicator)
  );
}

function hasContaminationWarning(ingredient: string): boolean {
  return CROSS_CONTAMINATION_WARNINGS.some(warning => 
    ingredient.toLowerCase().includes(warning.toLowerCase())
  );
}

function analyzeSpice(ingredient: string): {
  status: 'halal' | 'mushbooh';
  reason: string | null;
} {
  const lowerIngredient = ingredient.toLowerCase();

  // Check if it's a pure spice
  if (SPICE_CATEGORIES.PURE_SPICES.some(spice => 
    lowerIngredient === spice || 
    lowerIngredient === `ground ${spice}` || 
    lowerIngredient === `dried ${spice}`
  )) {
    return {
      status: 'halal',
      reason: null
    };
  }

  // Check for processed spices
  if (SPICE_CATEGORIES.PROCESSED_SPICES.some(spice => lowerIngredient.includes(spice))) {
    return {
      status: 'mushbooh',
      reason: 'Spice blend may contain processing aids or alcohol-based extracts. Verify halal certification.'
    };
  }

  // Check for concerning additives
  if (SPICE_CATEGORIES.SPICE_ADDITIVES.some(additive => lowerIngredient.includes(additive))) {
    return {
      status: 'mushbooh',
      reason: 'Contains additives that require verification (anti-caking agents, flow agents, etc.).'
    };
  }

  // Generic "spices" listing
  if (lowerIngredient === 'spices' || lowerIngredient === 'mixed spices') {
    return {
      status: 'mushbooh',
      reason: 'Generic spice listing - verify specific ingredients and processing methods.'
    };
  }

  // Default to mushbooh for any other spice-related ingredient
  return {
    status: 'mushbooh',
    reason: 'Spice ingredient requires verification of processing methods and additives.'
  };
}

function analyzeIngredientList(
  ingredients: string[],
  labels: string[] = []
): {
  status: Product['status'];
  statusReason: string;
  haramIngredients: string[];
  mushboohIngredients: string[];
} {
  const certification = isHalalCertified(labels);
  
  // If product has trusted halal certification, it overrides ingredient analysis
  if (certification.certified) {
    return {
      status: 'halal',
      statusReason: `Certified halal by ${certification.certifier?.toUpperCase()}`,
      haramIngredients: [],
      mushboohIngredients: []
    };
  }

  const haramIngredients: string[] = [];
  const mushboohIngredients: string[] = [];
  const contaminationWarnings: string[] = [];
  const spiceWarnings = new Set<string>();

  ingredients.forEach(ingredient => {
    const lowerIngredient = ingredient.toLowerCase();

    // Check for contamination warnings
    if (hasContaminationWarning(lowerIngredient)) {
      contaminationWarnings.push(ingredient);
      return;
    }

    // Special handling for spices
    if (lowerIngredient.includes('spice') || SPICE_CATEGORIES.PURE_SPICES.some(spice => lowerIngredient.includes(spice))) {
      const spiceAnalysis = analyzeSpice(ingredient);
      if (spiceAnalysis.status === 'mushbooh' && spiceAnalysis.reason) {
        // Add to spice warnings instead of mushbooh ingredients directly
        spiceWarnings.add(spiceAnalysis.reason);
      }
      return;
    }

    // Special handling for gelatin
    if (lowerIngredient.includes('gelatin') || lowerIngredient.includes('gelatine')) {
      // Only accept if explicitly labeled as halal
      if (!isHalalSource(lowerIngredient)) {
        haramIngredients.push(`${ingredient} (non-halal source)`);
        return;
      }
    }

    // Skip if ingredient is explicitly marked as halal source
    if (isHalalSource(lowerIngredient)) {
      return;
    }

    // Check for haram ingredients
    if (HARAM_INGREDIENTS.some(haram => lowerIngredient.includes(haram))) {
      haramIngredients.push(ingredient);
    }
    // Check for mushbooh ingredients
    else if (MUSHBOOH_INGREDIENTS.some(mushbooh => lowerIngredient.includes(mushbooh))) {
      // Special handling for vanilla extract to include fatwa reference
      if (lowerIngredient.includes('vanilla extract')) {
        mushboohIngredients.push(`${ingredient} (requires verification - <a href="https://islamqa.info/en/answers/177030" target="_blank" rel="noopener noreferrer">See IslamQA Fatwa</a>)`);
      } else {
        mushboohIngredients.push(ingredient);
      }
    }
  });

  // Add spice warnings to mushbooh ingredients if any exist
  if (spiceWarnings.size > 0) {
    mushboohIngredients.push(`Spices (${Array.from(spiceWarnings).join('; ')})`);
  }

  if (haramIngredients.length > 0) {
    let statusReason = `Contains haram ingredient${haramIngredients.length > 1 ? 's' : ''}: ${haramIngredients.join(', ')}. These ingredients are not permissible in Islam.`;
    if (contaminationWarnings.length > 0) {
      statusReason += ` Warning: ${contaminationWarnings.join('. ')}`;
    }
    return {
      status: 'haram',
      statusReason,
      haramIngredients,
      mushboohIngredients
    };
  } else if (mushboohIngredients.length > 0 || contaminationWarnings.length > 0) {
    const reasons = [
      ...mushboohIngredients.map(ingredient => {
        if (ingredient.toLowerCase().includes('enzyme')) {
          return `${ingredient} (source must be verified)`;
        } else if (ingredient.toLowerCase().includes('natural flavor')) {
          return `${ingredient} (verify if alcohol/animal-free)`;
        } else if (ingredient.toLowerCase().includes('glycerin') || ingredient.toLowerCase().includes('glycerol')) {
          return `${ingredient} (verify if plant-derived)`;
        } else if (ingredient.toLowerCase().includes('e471') || ingredient.toLowerCase().includes('mono') || ingredient.toLowerCase().includes('diglyceride')) {
          return `${ingredient} (verify if plant-derived)`;
        } else if (ingredient.toLowerCase().includes('lecithin')) {
          return `${ingredient} (verify source)`;
        } else if (ingredient.toLowerCase().includes('culture')) {
          return `${ingredient} (verify growth medium)`;
        } else if (ingredient.toLowerCase().includes('vanilla extract') || ingredient.toLowerCase().includes('spices')) {
          return ingredient; // Already includes explanation
        }
        return `${ingredient} (requires verification)`;
      }),
      ...contaminationWarnings
    ];

    return {
      status: 'mushbooh',
      statusReason: `Contains ingredients that require verification: ${reasons.join('; ')}. Please verify halal certification or contact manufacturer for source information.`,
      haramIngredients,
      mushboohIngredients
    };
  }

  return {
    status: 'halal',
    statusReason: 'All ingredients appear to be halal-compliant',
    haramIngredients,
    mushboohIngredients
  };
}

export async function fetchProductInfo(barcode: string): Promise<Partial<Product> | null> {
  try {
    const cleanBarcode = barcode.trim().replace(/[^0-9]/g, '');
    if (!cleanBarcode) {
      throw new Error('Invalid barcode');
    }

    const url = new URL(`${API_CONFIG.baseUrl}/api/v0/product/${cleanBarcode}.json`);
    url.searchParams.append('fields', API_CONFIG.fields);

    console.log('Fetching from OpenFoodFacts:', url.toString());

    const response = await fetch(url.toString(), {
      headers: API_CONFIG.headers,
    });

    if (!response.ok) {
      console.error('API response not ok:', response.status, await response.text());
      return null;
    }

    const data: OpenFoodFactsProduct = await response.json();

    if (!data.product || data.status !== 1) {
      console.log('No product data found:', data);
      return null;
    }

    let ingredients: string[] = [];
    
    if (data.product.ingredients && Array.isArray(data.product.ingredients)) {
      ingredients = data.product.ingredients.map(ing => ing.text);
    } else if (data.product.ingredients_text) {
      ingredients = data.product.ingredients_text
        .split(',')
        .map(i => i.trim())
        .filter(i => i.length > 0);
    }

    const labels = data.product.labels_tags || [];
    const certification = isHalalCertified(labels);

    // If product has trusted halal certification, it overrides ingredient analysis
    if (certification.certified) {
      return {
        barcode: cleanBarcode,
        name: data.product.product_name || 'Unknown Product',
        brand: data.product.brands || 'Unknown Brand',
        status: 'halal',
        statusReason: `Certified halal by ${certification.certifier?.toUpperCase()}`,
        ingredients,
        imageUrl: data.product.image_front_url || 
                 data.product.image_url || 
                 data.product.image_ingredients_url ||
                 'https://images.unsplash.com/photo-1553531384-cc64ac80f931?auto=format&fit=crop&q=80&w=300&h=300',
        certifiedBy: certification.certifier,
      };
    }

    // Analyze ingredients for E-numbers
    const eNumberAnalysis = await analyzeIngredients(ingredients);
    
    // Determine status based on E-numbers and regular ingredients
    const regularAnalysis = analyzeIngredientList(ingredients, labels);
    
    let status: Product['status'];
    let statusReason = '';

    if (eNumberAnalysis.haramENumbers.length > 0 || regularAnalysis.haramIngredients.length > 0) {
      status = 'haram';
      statusReason = [
        eNumberAnalysis.haramENumbers.length > 0 
          ? `Contains haram E-numbers: ${eNumberAnalysis.haramENumbers.map(e => e.e_code).join(', ')}`
          : null,
        regularAnalysis.haramIngredients.length > 0
          ? `Contains haram ingredients: ${regularAnalysis.haramIngredients.join(', ')}`
          : null
      ].filter(Boolean).join('. ');
    } else if (eNumberAnalysis.mushboohENumbers.length > 0 || regularAnalysis.mushboohIngredients.length > 0) {
      status = 'mushbooh';
      statusReason = [
        eNumberAnalysis.mushboohENumbers.length > 0
          ? `Contains E-numbers that require verification: ${eNumberAnalysis.mushboohENumbers.map(e => e.e_code).join(', ')}`
          : null,
        regularAnalysis.mushboohIngredients.length > 0
          ? regularAnalysis.statusReason
          : null
      ].filter(Boolean).join('. ');
    } else {
      status = 'halal';
      statusReason = 'All ingredients appear to be halal-compliant';
    }

    return {
      barcode: cleanBarcode,
      name: data.product.product_name || 'Unknown Product',
      brand: data.product.brands || 'Unknown Brand',
      status,
      statusReason,
      ingredients,
      imageUrl: data.product.image_front_url || 
                data.product.image_url || 
                data.product.image_ingredients_url ||
                'https://images.unsplash.com/photo-1553531384-cc64ac80f931?auto=format&fit=crop&q=80&w=300&h=300',
      certifiedBy: null,
    };
  } catch (error) {
    console.error('Error fetching product info:', error);
    return null;
  }
}